/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError, NodeTelemetryHistogramResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { buildOperationNamesParam } from './telemetryParams';

export const NODE_TELEMETRY_QUERY_KEY_ROOT = 'nodeTelemetry';

export type UseNodeTelemetryHistogramQueryParams = {
  nodeId: string;
  since: string;
  until: string;
  operationNames: string[];
};

export function useNodeTelemetryHistogramQuery({
  nodeId,
  since,
  until,
  operationNames,
}: UseNodeTelemetryHistogramQueryParams) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const operationNamesParam = buildOperationNamesParam(operationNames);

  return useQuery<NodeTelemetryHistogramResponse, ApiError>({
    queryKey: [NODE_TELEMETRY_QUERY_KEY_ROOT, 'histogram', tenantId, nodeId, since, until, operationNamesParam],
    enabled: !!tenantId && !!nodeId,
    placeholderData: keepPreviousData,
    queryFn: () =>
      mgmtPlaneAPIClient.nodes.getNodeTelemetryHistogram({
        tenantId: tenantId!,
        nodeId,
        since,
        until,
        operationNames: operationNamesParam,
      }),
  });
}
